import { useState } from 'react';
import { useList, useOne } from '@refinedev/core';
import { Check, ChevronsUpDown, Tag } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

interface PricingTierTranslationArrayItem {
  languageCode: string;
  name?: string | null;
  data?: { name?: string | null } | null;
}

type PricingTierTranslations =
  | Record<string, { name?: string | null } | undefined>
  | PricingTierTranslationArrayItem[];

export interface PricingTierOption {
  id: number;
  order: number;
  durationDays: number | null;
  prices: Record<string, number>;
  baseCurrency: string;
  isActive: boolean;
  courseId: number | null;
  subscriptionPlanId: number | null;
  channelPassId: number | null;
  translations?: PricingTierTranslations;
}

export function resolveTierName(tier: PricingTierOption): string {
  const translations = tier.translations;
  if (Array.isArray(translations)) {
    const ru = translations.find((t) => t.languageCode === 'ru');
    const name = ru?.name ?? ru?.data?.name;
    if (name) { return name; }
    const first = translations[0];
    const firstName = first?.name ?? first?.data?.name;
    if (firstName) { return firstName; }
  } else if (translations && typeof translations === 'object') {
    const ru = translations['ru'];
    if (ru?.name) { return ru.name; }
    const firstKey = Object.keys(translations)[0];
    if (firstKey) {
      const first = translations[firstKey];
      if (first?.name) { return first.name; }
    }
  }
  return `Тариф #${tier.id}`;
}

export function formatTierPrices(tier: PricingTierOption): string {
  const entries = Object.entries(tier.prices);
  if (entries.length === 0) { return '—'; }
  const base = tier.prices[tier.baseCurrency];
  if (base !== undefined) {
    const extras = entries
      .filter(([cur]) => cur !== tier.baseCurrency)
      .map(([cur, val]) => `${val.toLocaleString()} ${cur}`);
    const head = `${base.toLocaleString()} ${tier.baseCurrency}`;
    return extras.length > 0 ? `${head} (${extras.join(', ')})` : head;
  }
  return entries.map(([cur, val]) => `${val.toLocaleString()} ${cur}`).join(', ');
}

interface PricingTierSelectProps {
  value: number | null;
  onChange: (id: number | null) => void;
  disabled?: boolean;
  placeholder?: string;
  /** Показать превью (карточку) выбранного тарифа под селектором. */
  showPreview?: boolean;
  /** Разрешить сбросить выбор (nullable). По умолчанию true. */
  nullable?: boolean;
}

/**
 * Универсальный селектор существующего PricingTier.
 *
 * Загружает все активные тарифы через useList('pricing-tiers') и отображает
 * Combobox с поиском. После выбора показывает карточку с названием и ценами.
 *
 * Подходит для сущностей, ссылающихся на глобальный тариф по pricingTierId
 * (например, Book). Для курсов/абонементов/проходок тарифы управляются
 * через PricingTiersSection на странице самой сущности.
 */
export function PricingTierSelect({
  value,
  onChange,
  disabled,
  placeholder = 'Выберите тариф...',
  showPreview = true,
  nullable = true,
}: PricingTierSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const listResult = useList<PricingTierOption>({
    resource: 'pricing-tiers',
    pagination: { pageSize: 200 },
    queryOptions: { staleTime: 60_000 },
  });

  const tiers = listResult.result?.data ?? [];
  const selectedFromList = value !== null ? tiers.find((t) => t.id === value) ?? null : null;

  // Если выбранный тариф не попал в первую страницу — подгружаем отдельно
  const oneResult = useOne<PricingTierOption>({
    resource: 'pricing-tiers',
    id: value ?? '',
    queryOptions: { enabled: value !== null && !selectedFromList },
  });

  const selected = selectedFromList ?? oneResult.result ?? null;

  const filteredTiers = search
    ? tiers.filter((t) => {
      const name = resolveTierName(t).toLowerCase();
      return name.includes(search.toLowerCase()) || String(t.id).includes(search);
    })
    : tiers;

  return (
    <div className="space-y-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            aria-expanded={open}
            className="w-full justify-between"
            disabled={disabled}
          >
            <span className="truncate">
              {selected ? resolveTierName(selected) : placeholder}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Поиск тарифа..."
              value={search}
              onValueChange={setSearch}
            />
            <CommandList>
              <CommandEmpty>Тарифы не найдены</CommandEmpty>
              <CommandGroup>
                {nullable && (
                  <CommandItem
                    key="__none__"
                    value="__none__"
                    onSelect={() => {
                      onChange(null);
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        value === null ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                    <span className="text-muted-foreground">Без тарифа (бесплатно)</span>
                  </CommandItem>
                )}
                {filteredTiers.map((tier) => (
                  <CommandItem
                    key={tier.id}
                    value={String(tier.id)}
                    onSelect={() => {
                      onChange(tier.id);
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        value === tier.id ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                    <div className="flex flex-1 items-center justify-between gap-2">
                      <span className="truncate">{resolveTierName(tier)}</span>
                      <span className="text-xs text-muted-foreground shrink-0">
                        {formatTierPrices(tier)}
                      </span>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {showPreview && selected && (
        <div className="flex items-start gap-3 rounded-md border bg-muted/30 p-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded bg-muted">
            <Tag className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="min-w-0 flex-1 space-y-1">
            <p className="truncate text-sm font-medium">{resolveTierName(selected)}</p>
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="text-xs">
                {formatTierPrices(selected)}
              </Badge>
              {selected.durationDays !== null && (
                <span className="text-xs text-muted-foreground">
                  {selected.durationDays} дн.
                </span>
              )}
              {!selected.isActive && (
                <Badge variant="destructive" className="text-xs">Неактивен</Badge>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
